import { useState } from 'react';
import {
  AlertTriangle, Check, ChevronDown, ChevronRight, Copy, ExternalLink, X,
} from 'lucide-react';
import type { GscStand } from '../api';

/**
 * Wie eine Domain in die Search Console kommt — und ob sie schon drin ist.
 *
 * ══ WARUM ANLEITUNG UND STAND ZUSAMMEN ═════════════════════════════════════
 *
 * Eine Bestandsaufnahme, die nichts findet, sieht aus wie eine Domain ohne
 * indexierte Seiten. Sie kann aber genauso gut heissen: das Dienstkonto ist bei
 * dieser Property nicht eingetragen. Von aussen ist beides dasselbe leere
 * Ergebnis.
 *
 * Eine Anleitung allein beantwortet das nicht — sie sagt, was zu tun *wäre*,
 * nicht, ob es nötig ist. Deshalb steht oben der Befund und darunter der Weg,
 * und der Kasten macht sich nur von selbst auf, wenn etwas fehlt.
 *
 * ══ DER SCHRITT, DEN ALLE VERGESSEN ════════════════════════════════════════
 *
 * Verifizieren reicht nicht. Eine Property kann verifiziert sein und das
 * Dienstkonto sieht sie trotzdem nicht — weil es unter *Nutzer und
 * Berechtigungen* nicht eingetragen ist. Das sind zwei getrennte Schritte in
 * zwei getrennten Masken, und der zweite ist der, den man übersieht.
 *
 * Deshalb steht die Adresse des Dienstkontos hier zum Kopieren. Sie aus einer
 * JSON-Datei heraussuchen zu müssen ist die Hürde, an der es liegen bleibt.
 */
export default function SearchConsoleHilfe({
  stand,
  domain,
}: {
  stand?: GscStand;
  domain: string;
}) {
  // Offen, wenn etwas fehlt — zu, wenn alles steht. Ein Kasten, der immer
  // aufklappt, wird nach dem dritten Mal weggeklickt und dann auch dann nicht
  // gelesen, wenn er etwas zu sagen hätte.
  const alarm = Boolean(stand && (!stand.eingerichtet || !stand.erreichbar || !stand.property));
  const [offen, setOffen] = useState(alarm);
  const [kopiert, setKopiert] = useState(false);

  if (!stand) return null;

  const nackt = domain.replace(/^www\./i, '');

  return (
    <div
      className={`rounded-xl border ${
        alarm ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-surface'
      }`}
    >
      <button
        type="button"
        onClick={() => setOffen(!offen)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        {offen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {alarm ? (
          <AlertTriangle size={14} className="text-amber-600" />
        ) : (
          <Check size={14} className="text-emerald-600" />
        )}
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Search Console
        </span>
        <span className="text-sm text-slate-900">
          {!stand.eingerichtet
            ? 'kein Dienstkonto hinterlegt'
            : !stand.erreichbar
              ? 'Schlüssel wird abgelehnt'
              : stand.property
                ? stand.property
                : 'diese Domain ist nicht freigegeben'}
        </span>
      </button>

      {offen && (
        <div className="space-y-3 border-t border-slate-200 px-3 py-3 text-sm">
          {stand.hinweis && <p className="text-slate-500">{stand.hinweis}</p>}

          {/* ── Der Befund, wenn alles steht ──────────────────────────────── */}
          {stand.property && (
            <p className="text-slate-500">
              Diese Domain liegt unter{' '}
              <code className="rounded bg-slate-100 px-1 text-slate-900">{stand.property}</code>.
              Die Quelle <strong>Search Console</strong> liefert hier Daten.
            </p>
          )}

          {/* ── Die Adresse zum Eintragen ─────────────────────────────────── */}
          {stand.adresse && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Unser Dienstkonto
              </p>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 truncate rounded-lg bg-slate-100 px-2 py-1.5 text-xs text-slate-900">
                  {stand.adresse}
                </code>
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(stand.adresse ?? '');
                    setKopiert(true);
                  }}
                  className="rounded-lg border border-slate-300 px-2 text-slate-500 hover:text-slate-900"
                  title="Kopieren"
                >
                  {kopiert ? <Check size={13} /> : <Copy size={13} />}
                </button>
              </div>
            </div>
          )}

          {/* ── Der Weg ───────────────────────────────────────────────────── */}
          {!stand.property && stand.erreichbar && (
            <ol className="space-y-2 text-slate-500">
              <li>
                <strong className="text-slate-900">1. Property anlegen</strong>, falls es sie
                noch nicht gibt — als <em>Domain</em>-Property{' '}
                <code className="rounded bg-slate-100 px-1 text-slate-900">{nackt}</code>, nicht
                als URL-Präfix. Sie deckt alle Subdomains und beide Protokolle ab.
                <br />
                <a
                  href="https://search.google.com/search-console/welcome"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-brand-600 hover:underline"
                >
                  Search Console öffnen <ExternalLink size={11} />
                </a>
              </li>
              <li>
                <strong className="text-slate-900">2. Verifizieren</strong> über den
                TXT-Eintrag, den Google vorgibt. Bei einer Domain-Property geht nur DNS.
              </li>
              <li>
                <strong className="text-slate-900">3. Dienstkonto eintragen</strong> — der
                Schritt, der übersehen wird. Verifiziert heisst noch nicht, dass wir sie sehen.
                <br />
                Einstellungen → Nutzer und Berechtigungen → Nutzer hinzufügen → die Adresse von
                oben, Berechtigung <em>Eingeschränkt</em>.
                <br />
                <a
                  href="https://search.google.com/search-console/users"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-brand-600 hover:underline"
                >
                  Nutzer und Berechtigungen <ExternalLink size={11} />
                </a>
              </li>
              <li>
                <strong className="text-slate-900">4. Hier neu laden</strong> und die
                Bestandsaufnahme starten. Der Stand oben wird bei jedem Aufruf frisch geholt.
              </li>
            </ol>
          )}

          {/* ── Wenn gar kein Konto hinterlegt ist ────────────────────────── */}
          {!stand.eingerichtet && (
            <p className="text-slate-500">
              Unter <strong>Einstellungen → Suchmaschinen</strong> die JSON-Datei eines
              Google-Dienstkontos einfügen. Google gibt für die Search Console keinen
              API-Schlüssel aus — es geht nur über ein Dienstkonto.
            </p>
          )}

          {/* ── Was das Konto sonst sieht ─────────────────────────────────── */}
          {!stand.property && stand.bekannte.length > 0 && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Das Konto sieht bereits ({stand.bekannte.length})
              </p>
              {/* Nicht als Vollständigkeitsbeweis, sondern als Abgleich: steht
                  die Domain hier in anderer Schreibweise, ist es eine
                  URL-Präfix-Property statt einer Domain-Property. */}
              <ul className="max-h-24 space-y-0.5 overflow-y-auto text-xs text-slate-500">
                {stand.bekannte.map((b) => (
                  <li key={b} className="flex items-center gap-1">
                    <X size={10} className="shrink-0 text-slate-400" />
                    <code className="truncate">{b}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
